import { SearchPill } from '@kizen/kds/SearchPill';
import { Button } from '@kizen/kds/Button';
import { Spacer } from '@kizen/kds/Spacer';
import { TabConfig, TabMenu } from '@kizen/kds/Tabs';
import { Panel, VerticalTile } from '@kizen/kds/Tile';
import { Typography } from '@kizen/kds/Typography';
import { ColorType } from '@kizen/kds/Colors';
import { DEFAULT_RELATION_FIELDS_TABLE_HEIGHT } from 'components/Wizards/CustomObject/steps/CustomLayout/dialogs/LayoutComponentWizard/utilities';
import { isEqual } from 'lodash';
import { RelatedObjectFields as _RelatedObjectFields } from 'pages/Common/RelatedObjectFields';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { snakeToCamelCaseKeys } from 'services/helpers';
import { withErrorBoundary } from '../ErrorBoundary';
import { useIsFixedHeightRow } from '@kizen/kds/Grid';
import { NavIcon } from '__components/Kizen/Icon';

const RelatedObjectFields = _RelatedObjectFields as any;

interface RelatedObjectsBlockProps {
  customObjectModel: any;
  id: string;
  metadata: any;
  fields: any;
  displayName?: string;
  isClient?: boolean;
  clientObject?: any;
  touchedFormData: boolean;
  showAddRecordModal: boolean;
  resetAllFields: () => void;
  saveFormData: () => void;
  handleUpdateRecord: () => void;
  fieldState: any;
  refetch: () => void;
  javascriptActions?: any[];
}

interface ControllerOptions {
  options: {
    color?: ColorType;
    icon?: any;
    label: string;
    name?: string;
    objectType: string;
    value: string;
  }[];
  value: any;
  onChange: (tab: TabConfig) => void;
  search: string;
  setSearch: (...args: any[]) => any;
  loading: boolean;
  handleAddButtonClick: () => void;
  invalidAddRecordAccess: boolean;
  disableAddButton?: boolean;
}

const propsAreEqual = (prevProps: any, nextProps: any) => {
  return (
    prevProps.id === nextProps.id &&
    isEqual(prevProps.options, nextProps.options) &&
    isEqual(prevProps.value, nextProps.value) &&
    prevProps.search === nextProps.search &&
    prevProps.loading === nextProps.loading &&
    prevProps.displayName === nextProps.displayName &&
    prevProps.touchedFormData === nextProps.touchedFormData &&
    isEqual(prevProps.fieldState, nextProps.fieldState)
  );
};

const Block = memo((props: RelatedObjectsBlockProps) => {
  const {
    customObjectModel,
    id,
    metadata = {},
    displayName,
    isClient,
    clientObject,
    touchedFormData,
    resetAllFields,
    saveFormData,
    fields,
    handleUpdateRecord,
    fieldState,
    refetch,
    javascriptActions,
  } = props;
  const { t } = useTranslation();

  const expand = useIsFixedHeightRow();

  const body = (
    <div
      className={`w-full flex flex-col kds-menu-boundary ${expand ? 'h-full min-h-[var(--table-height)]' : 'h-[var(--table-height)]'}`}
      data-qa="related-objects-block"
      style={
        {
          '--table-height': `${
            metadata.tableHeight ?? DEFAULT_RELATION_FIELDS_TABLE_HEIGHT
          }px`,
        } as any
      }
    >
      <RelatedObjectFields
        objectModel={isClient ? clientObject : customObjectModel}
        clientObject={clientObject}
        entityId={id}
        refetch={refetch}
        fields={fields}
        meta={snakeToCamelCaseKeys(metadata)}
        javascriptActions={javascriptActions}
        frameless
        resetAllFields={resetAllFields}
        saveFormData={saveFormData}
        handleUpdateRecord={handleUpdateRecord}
        touchedFormData={touchedFormData}
        fieldState={fieldState}
        controller={({
          options = [],
          value,
          onChange,
          search,
          setSearch,
          loading,
          handleAddButtonClick,
          invalidAddRecordAccess,
          disableAddButton,
        }: ControllerOptions) => {
          const showAddRecordButton =
            metadata?.allowAddRecordButtonById?.[value?.value];
          return (
            <div className="kds pt-spacer-15">
              <div className="flex items-center">
                <div className="flex min-w-0">
                  <Spacer mode="vertical" size={20} />
                  <Typography
                    variant="header"
                    weight="semibold"
                    size="lg"
                    truncate
                    className="max-w-[250px] min-w-0"
                  >
                    {displayName || t('Related')}
                  </Typography>
                </div>
                <Spacer mode="vertical" size={20} />
                <div className="shrink grow">
                  <TabMenu
                    tabs={options.map((option) => {
                      return {
                        color: option.color,
                        icon: option.icon || 'bars-light',
                        children: option.name ?? option.label,
                        id: option.value,
                        value: option.value,
                      };
                    })}
                    onChange={onChange}
                    value={value?.value}
                    renderIcon={(props) => (
                      <NavIcon
                        size="1x"
                        style={{ color: 'inherit', fontSize: '14px' }}
                        {...props}
                      />
                    )}
                  />
                </div>
                <Spacer mode="vertical" size={10} />
                <div className="ml-auto flex items-center min-w-0 gap-[15px]">
                  <SearchPill
                    placeholder={t('Search')}
                    value={search}
                    onChange={(v) => setSearch(v)}
                    loading={loading}
                  />
                  {showAddRecordButton && !invalidAddRecordAccess ? (
                    <Button
                      variant="text"
                      leftIcon="action-add"
                      onClick={handleAddButtonClick}
                      disabled={disableAddButton}
                      qa={{
                        action: 'add-related-pipeline-record',
                      }}
                    >
                      {t('Add')}
                    </Button>
                  ) : null}
                  <Spacer mode="vertical" size={5} />
                </div>
              </div>
            </div>
          );
        }}
      />
    </div>
  );

  return <VerticalTile padding={0} middle={<Panel expand>{body}</Panel>} />;
}, propsAreEqual);

export const RelatedObjectsBlock = withErrorBoundary(Block);
